import React, { useState, useEffect } from "react"
import {
  Container,
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Input,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import InputMask from "react-input-mask"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  getParceiros,
  createParceiro,
  updateParceiro,
  deleteParceiro,
} from "helpers/parceiro_helper"
import { useHistory } from "react-router-dom"
import { FaEdit, FaTrash, FaPlus, FaLayerGroup } from "react-icons/fa"
import { Pagination } from "../Monitoramento/PaginationComponent"
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const TIPO_LABELS = {
  seguradora: "Seguradora",
  corretora: "Corretora",
}

const formVazio = {
  Nome: "",
  Tipo: "seguradora",
  Cnpj: "",
  CodigoSusep: "",
}

const Parceiros = () => {
  document.title = "Parceiros | Blops Leads"

  const [parceiros, setParceiros] = useState([])
  const [loading, setLoading] = useState(false)
  const [filtroTipo, setFiltroTipo] = useState("")
  const [busca, setBusca] = useState("")

  const [modalAberto, setModalAberto] = useState(false)
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(formVazio)
  const [salvando, setSalvando] = useState(false)

  const history = useHistory()
  const [paginaAtual, setPaginaAtual] = useState(1)
  const itensPorPagina = 10

  useEffect(() => {
    carregarParceiros()
    setPaginaAtual(1)
  }, [filtroTipo])

  useEffect(() => {
    setPaginaAtual(1)
  }, [busca])

  const carregarParceiros = async () => {
    setLoading(true)
    try {
      const data = await getParceiros(filtroTipo || undefined)
      setParceiros(data)
    } catch {
      toastr.error("Erro ao carregar parceiros", "Erro")
    } finally {
      setLoading(false)
    }
  }

  const abrirModalNovo = () => {
    setEditando(null)
    setForm(formVazio)
    setModalAberto(true)
  }

  const abrirModalEditar = (p) => {
    setEditando(p)
    setForm({
      Nome: p.Nome,
      Tipo: p.Tipo,
      Cnpj: p.Cnpj || "",
      CodigoSusep: p.CodigoSusep || "",
    })
    setModalAberto(true)
  }

  const fecharModal = () => {
    setModalAberto(false)
    setEditando(null)
    setForm(formVazio)
  }

  const handleSalvar = async () => {
    if (!form.Nome.trim()) {
      toastr.warning("Por favor, informe o nome do parceiro", "Atenção")
      return
    }
    const cnpjLimpo = form.Cnpj.replace(/[^\d]/g, "")
    if (cnpjLimpo.length < 14) {
      toastr.warning("Por favor, informe o CNPJ completo do parceiro", "Atenção")
      return
    }

    const payload = {
      Nome: form.Nome.trim(),
      Tipo: form.Tipo,
      Cnpj: form.Cnpj.trim() || null,
      CodigoSusep: form.CodigoSusep.trim() || null,
    }

    setSalvando(true)
    try {
      if (editando) {
        await updateParceiro(editando.Id, payload)
        toastr.success("Parceiro atualizado com sucesso!", "Sucesso")
      } else {
        await createParceiro(payload)
        toastr.success("Parceiro criado com sucesso!", "Sucesso")
      }
      fecharModal()
      carregarParceiros()
    } catch (error) {
      toastr.error(error.response?.data?.message || "Erro ao salvar parceiro", "Erro")
    } finally {
      setSalvando(false)
    }
  }

  const handleExcluir = async (id) => {
    if (window.confirm("Deseja realmente excluir este parceiro?")) {
      try {
        await deleteParceiro(id)
        toastr.success("Parceiro excluído com sucesso!", "Sucesso")
        carregarParceiros()
      } catch (error) {
        toastr.error(error.response?.data?.message || "Erro ao excluir parceiro", "Erro")
      }
    }
  }

  const parceirosFiltrados = parceiros.filter(p => {
    const termo = busca.toLowerCase()
    return (
      p.Nome.toLowerCase().includes(termo) ||
      (p.Cnpj && p.Cnpj.toLowerCase().includes(termo)) ||
      (p.CodigoSusep && p.CodigoSusep.toLowerCase().includes(termo))
    )
  })
  const totalPaginas = Math.ceil(parceirosFiltrados.length / itensPorPagina)
  const parceirosExibidos = parceirosFiltrados.slice(
    (paginaAtual - 1) * itensPorPagina,
    paginaAtual * itensPorPagina
  )

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Cadastros" breadcrumbItem="Parceiros" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div className="d-flex align-items-center gap-3">
                      <h4 className="card-title mb-0">Parceiros</h4>
                      <Input
                        type="text"
                        placeholder="Buscar por nome, CNPJ ou código SUSEP..."
                        style={{ width: 220 }}
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                      />
                      <Input
                        type="select"
                        style={{ width: 160 }}
                        value={filtroTipo}
                        onChange={e => setFiltroTipo(e.target.value)}
                      >
                        <option value="">Todos os tipos</option>
                        <option value="seguradora">Seguradoras</option>
                        <option value="corretora">Corretoras</option>
                      </Input>
                    </div>
                    <Button color="primary" onClick={abrirModalNovo}>
                      <FaPlus className="me-1" /> Novo Parceiro
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Carregando...</span>
                      </div>
                    </div>
                  ) : parceiros.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>Nenhum parceiro cadastrado.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Nome</th>
                            <th style={{ width: 120 }}>Tipo</th>
                            <th>CNPJ</th>
                            <th>Código SUSEP</th>
                            <th style={{ width: 130 }}>Template Ativo</th>
                            <th style={{ width: 140 }} className="text-center">Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parceirosExibidos.map(p => (
                            <tr key={p.Id}>
                              <td>{p.Nome}</td>
                              <td>
                                <Badge color={p.Tipo === "seguradora" ? "primary" : "info"}>
                                  {TIPO_LABELS[p.Tipo] || p.Tipo}
                                </Badge>
                              </td>
                              <td>{p.Cnpj || <small className="text-muted">—</small>}</td>
                              <td>{p.CodigoSusep || <small className="text-muted">—</small>}</td>
                              <td>
                                {p.Templates?.find(t => t.Ativo) ? (
                                  <Badge color="success">
                                    {p.Templates.find(t => t.Ativo).Nome}
                                  </Badge>
                                ) : (
                                  <small className="text-muted">—</small>
                                )}
                              </td>
                              <td className="text-center">
                                <Button
                                  color="secondary"
                                  size="sm"
                                  className="me-1"
                                  title="Templates"
                                  onClick={() => history.push(`/parceiros/${p.Id}/templates`)}
                                >
                                  <FaLayerGroup />
                                </Button>
                                <Button
                                  color="info"
                                  size="sm"
                                  className="me-1"
                                  title="Editar"
                                  onClick={() => abrirModalEditar(p)}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  title="Excluir"
                                  onClick={() => handleExcluir(p.Id)}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}

                  {!loading && totalPaginas > 1 && (
                    <div className="d-flex flex-column align-items-end mt-2">
                      <small className="text-muted mb-2">
                        Exibindo {(paginaAtual - 1) * itensPorPagina + 1}–
                        {Math.min(paginaAtual * itensPorPagina, parceirosFiltrados.length)} de {parceirosFiltrados.length} parceiros
                      </small>
                      <Pagination
                        currentPage={paginaAtual}
                        totalPages={totalPaginas}
                        onPageChange={setPaginaAtual}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Modal isOpen={modalAberto} toggle={fecharModal} size="md">
        <ModalHeader toggle={fecharModal}>
          {editando ? "Editar Parceiro" : "Novo Parceiro"}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Tipo *</label>
            <Input
              type="select"
              value={form.Tipo}
              onChange={e => setForm(f => ({ ...f, Tipo: e.target.value }))}
              disabled={salvando}
            >
              <option value="seguradora">Seguradora</option>
              <option value="corretora">Corretora</option>
            </Input>
          </div>
          <div className="mb-3">
            <label className="form-label">Nome *</label>
            <Input
              type="text"
              placeholder="Nome do parceiro"
              value={form.Nome}
              onChange={e => setForm(f => ({ ...f, Nome: e.target.value }))}
              disabled={salvando}
            />
          </div>
          <Row className="mb-3">
            <Col md={6}>
              <label className="form-label">CNPJ <span>*</span></label>
              <InputMask
                mask="99.999.999/9999-99"
                className="form-control"
                placeholder="00.000.000/0001-00"
                value={form.Cnpj}
                onChange={e => setForm(f => ({ ...f, Cnpj: e.target.value }))}
                disabled={salvando}
              />
            </Col>
            <Col md={6}>
              <label className="form-label">Código SUSEP</label>
              <Input
                type="text"
                placeholder="Código SUSEP"
                value={form.CodigoSusep}
                onChange={e => setForm(f => ({ ...f, CodigoSusep: e.target.value }))}
                disabled={salvando}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={fecharModal} disabled={salvando}>
            Cancelar
          </Button>
          <Button color="primary" onClick={handleSalvar} disabled={salvando}>
            {salvando ? "Salvando..." : editando ? "Atualizar" : "Criar"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default Parceiros
